import type { FC } from "react";

import type { OCRUploadedFile } from "../OCRUploadCard";
import { Card } from "../../Card";
import { Flex } from "../../Flex";
import { Icon } from "../../Icon";
import { IconButton } from "../../IconButton";
import { ProgressBar } from "../../ProgressBar";
import { Text } from "../../Text";
import { StepChip } from "./StepChip";

import { contentStyle, stepsContainerStyle, rowStyle } from "./FileRow.css";
import { Button } from "../../Button";
import { Tooltip } from "../../Tooltip";

export type FileRowProps = {
  /**
   * The uploaded file with OCR processing status
   */
  file: OCRUploadedFile;
  /**
   * Whether the file is currently uploading (shows progress bar)
   */
  loading?: boolean;
  /**
   * Callback when view button is clicked
   */
  onView?: (fileId: string) => void;
  /**
   * Callback when delete button is clicked
   */
  onDelete?: (fileId: string) => void;
  /**
   * Callback when retry button is clicked
   */
  onRetry?: (fileId: string) => void;
};

/**
 * Renders a file row with its OCR processing status
 */
export const FileRow: FC<FileRowProps> = ({
  file,
  loading,
  onView,
  onDelete,
  onRetry,
}) => {
  const isError = file.status === "error";
  const isSuccess = file.status === "success";
  const isProcessing = file.status === "in-progress";

  return (
    <Card
      padding="xs"
      borderRadius="s"
      border={isProcessing && !loading ? "gradientAnimated" : "default"}
      innerClassName={rowStyle({ isError })}
      contentClassName={contentStyle}
      overflowHidden
    >
      <Icon icon="document" size="s2" />

      <Flex direction="column" grow gap="xxs2" overflowHidden>
        <Text variant="bodyS" ellipsis bold>
          {file.name}
        </Text>
        {isError && (
          <Text variant="bodyXS" color="error">
            {file.errorMessage ?? "Upload mislukt"}
          </Text>
        )}
      </Flex>

      {loading && <ProgressBar variant="infinite" position="absolute" />}

      {!loading &&
        (isProcessing || isSuccess) &&
        file.steps &&
        file.steps.length > 0 && (
          <Flex gap="s" className={stepsContainerStyle}>
            {file.steps.map((step, index) => (
              <StepChip key={index} status={step.status} label={step.label} />
            ))}
          </Flex>
        )}

      {isSuccess && (onView || onDelete) && (
        <Flex gap="xs">
          {onView && (
            <IconButton
              icon="eye"
              variant="tertiary"
              size="s"
              onClick={() => onView(file.id)}
            />
          )}
          {onDelete && (
            <IconButton
              icon="delete"
              variant="tertiary"
              size="s"
              onClick={() => onDelete(file.id)}
            />
          )}
        </Flex>
      )}

      {isError && onRetry && (
        <Button
          size="xs"
          variant="background"
          border="default"
          trailing={<Icon icon="reload" size="s" />}
          onClick={() => onRetry(file.id)}
        >
          Ander bestand proberen
        </Button>
      )}

      {isSuccess && onRetry && (
        <Tooltip value="Ander bestand proberen">
          <IconButton
            icon="reload"
            onClick={() => onRetry(file.id)}
            size="xs"
            variant="background"
            border="default"
          />
        </Tooltip>
      )}
    </Card>
  );
};

FileRow.displayName = "FileRow";
